import type { EntryMetadata } from '../core/types.js'

/** How the keybook gallery orders its keys — inside each section (yours / shared with you / the org's). */
export type KeySort = 'name' | 'recentlyUsed' | 'recentlyUpdated' | 'newest'

export const KEY_SORTS: readonly KeySort[] = ['name', 'recentlyUsed', 'recentlyUpdated', 'newest']
export const DEFAULT_KEY_SORT: KeySort = 'name'

/** Per viewer, per browser (a convenience — a blocked storage just means the default). */
const STORAGE_KEY = 'papan.keybook.sort'

export function readKeySort(): KeySort {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return (KEY_SORTS as readonly string[]).includes(v ?? '') ? (v as KeySort) : DEFAULT_KEY_SORT
  } catch {
    return DEFAULT_KEY_SORT
  }
}

export function writeKeySort(sort: KeySort): void {
  try { localStorage.setItem(STORAGE_KEY, sort) } catch { /* the choice just isn't remembered */ }
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/** The name the fob shows — the shared name, as engraved on the card (a nickname only slides out on hover). */
const shownName = (e: EntryMetadata) => e.sourceLabel ?? e.label

const byName = (a: EntryMetadata, b: EntryMetadata) => collator.compare(shownName(a), shownName(b)) || a.id.localeCompare(b.id)

/** Newest first; a key never used (or a missing date) sorts after every dated one, then by name. */
const byDateDesc = (pick: (e: EntryMetadata) => string | null | undefined) => (a: EntryMetadata, b: EntryMetadata) => {
  const ta = Date.parse(pick(a) ?? '')
  const tb = Date.parse(pick(b) ?? '')
  const ha = Number.isFinite(ta)
  const hb = Number.isFinite(tb)
  if (ha && hb && ta !== tb) return tb - ta
  if (ha !== hb) return ha ? -1 : 1
  return byName(a, b)
}

const COMPARE: Record<KeySort, (a: EntryMetadata, b: EntryMetadata) => number> = {
  name: byName,
  recentlyUsed: byDateDesc(e => e.lastUsedAt),
  recentlyUpdated: byDateDesc(e => e.updatedAt),
  newest: byDateDesc(e => e.createdAt),
}

/** A sorted copy. */
export function sortKeys(entries: readonly EntryMetadata[], sort: KeySort): EntryMetadata[] {
  return [...entries].sort(COMPARE[sort])
}
